import React from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import Loader from 'halogen/PulseLoader';
import { injectIntl, intlShape } from 'react-intl';
import css from './index.scss';
import StudentHeader from '../student-header/index';
import StudentFilter from '../../components/student-filter/index';
import { StudentRowRenderer } from '../../components/renderers/index';
import * as gridFormatters from '../../components/formatters/index';
import EmptyState from '../../components/empty-state/index';
import StudentFooter from '../../components/student-footer/index';
import Modal from '../../components/modal/index';
import AddStudent from '../../components/add-student/index';
import { StudentManagement, Tick } from '../../helpers/icon-dictionary';
import AddStudentConfirmation from '../../components/student-confirmation/index';
import DataGrid from '../../components/data-grid/index';
import Messages from './messages';

function getGridColumns(selectedProduct, handleGridSort, handleOnStudentEdit, intl, handleOnAssignClass) {
    return [
        {
            key: 'firstName',
            name: `${intl.formatMessage(Messages.firstName)}`,
            resizable: true,
            width: 150,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'lastName',
            name: `${intl.formatMessage(Messages.surname)}`,
            resizable: true,
            width: 150,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'yearGroupEnum',
            name: `${intl.formatMessage(Messages.year)}`,
            sortable: true,
            width: 70,
            formatter: gridFormatters.YearGroupFormatter,
            getRowMetaData: row => row,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'userName',
            name: `${intl.formatMessage(Messages.username)}`,
            width: 200,
            sortable: true,
            resizable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'password',
            name: `${intl.formatMessage(Messages.password)}`,
            width: 130,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'class',
            name: `${selectedProduct.name.toUpperCase()} ${intl.formatMessage(Messages.classes)}`,
            width: 200,
            resizable: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.AssignedClassFormatter handleOnAssignClass={handleOnAssignClass} />
        },
        {
            key: 'group',
            width: 200,
            name: `${intl.formatMessage(Messages.groups)}`,
            resizable: true,
            formatter: gridFormatters.AssignedGroupFormatter
        },
        {
            key: 'teachers',
            name: `${intl.formatMessage(Messages.teachers)}`,
            width: 200,
            resizable: true,
            formatter: gridFormatters.AssignedGroupFormatter
        },
        {
            key: '',
            name: ''
        },
        {
            key: 'edit',
            name: `${intl.formatMessage(Messages.edit)}`,
            width: 70,
            locked: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.EditInfoFormatter handleOnEdit={handleOnStudentEdit} />
        }
    ];
}

const StudentRoll = ({
    students,
    studentsCount,
    selectedStudents = [],
    isStudentDataLoaded,
    isStudentRollDownloaded,
    gridRowGetter,
    onGridSort,
    onRowSelect,
    onDownloadStudentRoll,
    onQuickUpload,
    onAddStudent,
    onAddAnotherStudent,
    dataFilterOptions,
    filterOnChange,
    filterOnClear,
    onSearch,
    selectedProduct,
    handleOnStudentUnassign,
    showModal,
    handleOnCloseModal,
    handleOnUnassignConfirm,
    isUserOperationModalOpen,
    studentsOperationSuccessConfirmation,
    unassignStudentsSuccessConfirmation,
    handleOnStudentBulkPasswordChange,
    handleOnStudentBulkPasswordChangeCloseModal,
    handleOnBulkPasswordChangeConfirm,
    bulkPasswordChangeSuccessConfirmation,
    handleOnChangeBulkPasswordSetValue,
    bulkPasswordValue,
    isLoading,
    showAddStudentsModal,
    onAddStudentCloseModal,
    classrooms,
    student,
    isSaveInProgress,
    handleOnAddStudentSubmit,
    showAddStudentValidationModal,
    addStudentValidationMessage,
    closeAddStudentValidationModal,
    showAddStudentConfirmationModal,
    isSignInCardDownloaded,
    handleOnPrintSignInCard,
    handleOnPrintMultipleSignInCard,
    handleOnStudentEdit,
    intl,
    handleOnAssignClass,
    filteredStudentsCount,
    handleOnStudentDeleteConfirmation,
    handleOnStudentDeleteClose,
    isDeleteUserModalOpen,
    handleOnDeleteStudent,
    operationType,
    handleOnDeleteConfirmation,
    classroomGroups,
    getClassroomGroups,
    downloadStudentsSignInCards,
    showHideSignInCardModal,
    handleOnStudentSignInCards,
    handleOnCancelStudentSignInCards
}) => {
    const loaderView = (
        <div className={css.studentListContainer}>
            <div className={css.loadingContainer}>
                <Loader className={css.loader} />
            </div>
        </div>);

    const loadAddStudentModalComponent = () => {
        let modalComponent = '';
        if (showAddStudentConfirmationModal) {
            modalComponent = (
                <AddStudentConfirmation
                    handleOnAddStudent={onAddAnotherStudent}
                    handleOnCancelStudentSignInCards={handleOnCancelStudentSignInCards}
                    handleOnCloseModal={onAddStudentCloseModal}
                    handleOnPrintSignInCard={handleOnPrintSignInCard}
                    handleOnStudentSignInCards={handleOnStudentSignInCards}
                    icon={Tick}
                    isSignInCardDownloaded={isSignInCardDownloaded}
                    showHideSignInCardModal={showHideSignInCardModal}
                    title={intl.formatMessage(Messages.addStudentConfirmation)}
                />
            );
        } else {
            const yearGroups = cloneDeep(dataFilterOptions.yearGroups);
            yearGroups[0].value = intl.formatMessage(Messages.yearGroupList);
            modalComponent = (
                <AddStudent
                    addStudentValidationMessage={addStudentValidationMessage}
                    classes={classrooms}
                    classroomGroups={classroomGroups}
                    closeAddStudentValidationModal={closeAddStudentValidationModal}
                    getClassroomGroups={getClassroomGroups}
                    handleOnAddStudentSubmit={handleOnAddStudentSubmit}
                    handleOnCloseModal={onAddStudentCloseModal}
                    isSaveInProgress={isSaveInProgress}
                    selectedProduct={selectedProduct}
                    showAddStudentValidationModal={showAddStudentValidationModal}
                    student={student}
                    yearGroups={yearGroups}
                />);
        }

        return modalComponent;
    };

    const addStudentModalView = (
        <Modal show={showAddStudentsModal} >
            {loadAddStudentModalComponent()}
        </Modal>
    );

    dataFilterOptions.classrooms[0].value = intl.formatMessage(Messages.assignedStudents);
    dataFilterOptions.classrooms[1].value = intl.formatMessage(Messages.allClasses);
    dataFilterOptions.classrooms[2].value = intl.formatMessage(Messages.unassignedStudents);

    const emptyStateView = (
        <div className={css.studentListContainer}>
            <StudentFilter
                classrooms={dataFilterOptions.classrooms}
                handleFilterOnChange={filterOnChange}
                handleFilterOnClear={filterOnClear}
                handleOnSearch={onSearch}
                lastNames={dataFilterOptions.lastNames}
                searchTerm={dataFilterOptions.searchTerm}
                yearGroups={dataFilterOptions.yearGroups} />
            <EmptyState
                iconName={StudentManagement}
                message={intl.formatMessage(Messages.noStudentsAvailable)} />
            {addStudentModalView}
        </div>);

    const dataView = (
        <div className={css.studentListContainer}>
            <StudentFilter
                classrooms={dataFilterOptions.classrooms}
                handleFilterOnChange={filterOnChange}
                handleFilterOnClear={filterOnClear}
                handleOnSearch={onSearch}
                lastNames={dataFilterOptions.lastNames}
                searchTerm={dataFilterOptions.searchTerm}
                yearGroups={dataFilterOptions.yearGroups} />
            <DataGrid
                columns={getGridColumns(selectedProduct, onGridSort, handleOnStudentEdit, intl, handleOnAssignClass)}
                handleGridSort={onGridSort}
                handleRowSelect={onRowSelect}
                overScan={{ colsStart: 17, colsEnd: 17, rowsStart: 25, rowsEnd: 25 }}
                rowGetter={gridRowGetter}
                rowHeight={150}
                rowKey="userProfileId"
                rowRenderer={StudentRowRenderer}
                rowsCount={students.length}
                selectedRows={selectedStudents.length > 0 ? selectedStudents : []}
            />
            {addStudentModalView}
        </div>);

    let finalView;
    if (!isStudentDataLoaded) {
        finalView = loaderView;
    } else {
        finalView = filteredStudentsCount === 0 ? emptyStateView : dataView;
    }
    const studentsComponent = (<div className={css.studentRollComponent}>
        <StudentHeader
            filteredStudentsCount={students.length}
            handleAddStudent={onAddStudent}
            handleOnDownloadStudentRoll={onDownloadStudentRoll}
            handleQuickUpload={onQuickUpload}
            isStudentDataLoaded={isStudentDataLoaded}
            isStudentRollDownloaded={isStudentRollDownloaded}
            studentsCountInSchool={studentsCount} />
        {finalView}
        <StudentFooter
            bulkPasswordChangeSuccessConfirmation={bulkPasswordChangeSuccessConfirmation}
            bulkPasswordValue={bulkPasswordValue}
            downloadStudentsSignInCards={downloadStudentsSignInCards}
            handleOnBulkPasswordChangeConfirm={handleOnBulkPasswordChangeConfirm}
            handleOnChangeBulkPasswordSetValue={handleOnChangeBulkPasswordSetValue}
            handleOnCloseModal={handleOnCloseModal}
            handleOnDeleteConfirmation={handleOnDeleteConfirmation}
            handleOnDeleteStudent={handleOnDeleteStudent}
            handleOnDownloadSignInCards={handleOnPrintMultipleSignInCard}
            handleOnStudentBulkPasswordChange={handleOnStudentBulkPasswordChange}
            handleOnStudentBulkPasswordChangeCloseModal={handleOnStudentBulkPasswordChangeCloseModal}
            handleOnStudentDeleteClose={handleOnStudentDeleteClose}
            handleOnStudentDeleteConfirmation={handleOnStudentDeleteConfirmation}
            handleOnStudentUnassign={handleOnStudentUnassign}
            handleOnUnassignConfirm={handleOnUnassignConfirm}
            isDeleteUserModalOpen={isDeleteUserModalOpen}
            isLoading={isLoading}
            isSignInCardDownloaded={isSignInCardDownloaded}
            isUserOperationModalOpen={isUserOperationModalOpen}
            noOfSelectedStudents={selectedStudents.length}
            operationType={operationType}
            showModal={showModal}
            studentsOperationSuccessConfirmation={studentsOperationSuccessConfirmation}
            unassignStudentsSuccessConfirmation={unassignStudentsSuccessConfirmation}
        />
    </div>);

    return (
        <div className={css.studentRollComponent}>
            { studentsComponent }
        </div>
    );
};

StudentRoll.propTypes = {
    isStudentDataLoaded: PropTypes.bool,
    isStudentRollDownloaded: PropTypes.bool,
    showAddStudentConfirmationModal: PropTypes.bool,
    student: PropTypes.object,
    students: PropTypes.arrayOf(PropTypes.object),
    classrooms: PropTypes.arrayOf(PropTypes.object),
    selectedStudents: PropTypes.arrayOf(PropTypes.number),
    gridRowGetter: PropTypes.func,
    studentsCount: PropTypes.number,
    filteredStudentsCount: PropTypes.number,
    onGridSort: PropTypes.func,
    onRowSelect: PropTypes.func,
    onDownloadStudentRoll: PropTypes.func,
    onQuickUpload: PropTypes.func,
    onAddStudent: PropTypes.func,
    closeAddStudentValidationModal: PropTypes.func,
    dataFilterOptions: PropTypes.object,
    filterOnChange: PropTypes.func,
    filterOnClear: PropTypes.func,
    onSearch: PropTypes.func,
    onAddStudentCloseModal: PropTypes.func,
    handleOnAddStudentSubmit: PropTypes.func,
    selectedProduct: PropTypes.object,
    showAddStudentsModal: PropTypes.bool,
    isSaveInProgress: PropTypes.bool,
    showAddStudentValidationModal: PropTypes.bool,
    isSignInCardDownloaded: PropTypes.bool,
    addStudentValidationMessage: PropTypes.string,
    handleOnPrintSignInCard: PropTypes.func,
    handleOnPrintMultipleSignInCard: PropTypes.func,
    handleOnStudentUnassign: PropTypes.func,
    handleOnCloseModal: PropTypes.func,
    showModal: PropTypes.bool,
    handleOnUnassignConfirm: PropTypes.func,
    isUserOperationModalOpen: PropTypes.bool,
    isLoading: PropTypes.bool,
    studentsOperationSuccessConfirmation: PropTypes.func,
    unassignStudentsSuccessConfirmation: PropTypes.func,
    handleOnStudentBulkPasswordChange: PropTypes.func,
    handleOnStudentBulkPasswordChangeCloseModal: PropTypes.func,
    handleOnBulkPasswordChangeConfirm: PropTypes.func,
    handleOnChangeBulkPasswordSetValue: PropTypes.func,
    bulkPasswordChangeSuccessConfirmation: PropTypes.func,
    bulkPasswordValue: PropTypes.string,
    onAddAnotherStudent: PropTypes.func,
    handleOnStudentEdit: PropTypes.func,
    intl: intlShape.isRequired,
    handleOnAssignClass: PropTypes.func,
    handleOnStudentDeleteConfirmation: PropTypes.func,
    handleOnStudentDeleteClose: PropTypes.func,
    isDeleteUserModalOpen: PropTypes.bool,
    handleOnDeleteStudent: PropTypes.func,
    operationType: PropTypes.string,
    handleOnDeleteConfirmation: PropTypes.func,
    classroomGroups: PropTypes.arrayOf(PropTypes.object),
    getClassroomGroups: PropTypes.func,
    downloadStudentsSignInCards: PropTypes.func,
    showHideSignInCardModal: PropTypes.bool,
    handleOnStudentSignInCards: PropTypes.func,
    handleOnCancelStudentSignInCards: PropTypes.func
};

export default injectIntl(styleable(css)(StudentRoll));



// WEBPACK FOOTER //
// ./app/components/student-roll/index.js